import behaviourCoreValueConstants from "@/constants/behaviourCoreValueConstants.js"
import behaviourManagementConstants from "@/constants/behaviourManagementConstants.js"
import colorPickerPaletteConstants from "@/constants/colorPickerPaletteConstants.js"

const { MAX_NAME_LENGTH, MAX_DESCRIPTION_LENGTH } = behaviourCoreValueConstants

/**
 * @returns {{ name: string, description: string, category_color: string, status: number }}
 */
export function createEmptyCoreValueForm() {
  return {
    name: "",
    description: "",
    category_color: "",
    status: 1
  }
}

/**
 * @param {string} category API `category` code (e.g. "W") or legacy hex.
 * @returns {string}
 */
export function resolveCategoryColor(category) {
  return colorPickerPaletteConstants.resolveCoreValueCategoryHex(category)
}

/**
 * @param {string} hex
 * @returns {'green' | 'blue'}
 */
export function mapCategoryColorToIndicator(hex) {
  const normalized = String(hex || "").toLowerCase()
  const blueHints = ["#7986cb", "#567593", "#039be5", "#3f51b5", "#a19af5"]
  if (blueHints.includes(normalized)) {
    return behaviourManagementConstants.CORE_VALUE_INDICATOR_COLORS.BLUE
  }
  return behaviourManagementConstants.CORE_VALUE_INDICATOR_COLOR_DEFAULT
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {{ name: string, description: string, category_color: string, status: number }}
 */
export function coreValueRowToForm(row) {
  if (!row) return createEmptyCoreValueForm()
  const isActive =
    row.status === "active" || row.status === 1 || row.status === true
  const apiCategory = String(row.category ?? row.category_color ?? "").trim()
  return {
    name: String(row.name ?? ""),
    description: String(row.description ?? ""),
    category: apiCategory,
    category_color: resolveCategoryColor(apiCategory),
    sort_order: row?.sort_order ?? null,
    status: isActive ? 1 : 0
  }
}

/**
 * @param {{ category?: string, category_color?: string }} form
 * @returns {string}
 */
export function resolveCategoryForApi(form) {
  const fromPicker = String(form?.category_color ?? "").trim()
  if (fromPicker.startsWith("#")) {
    const code =
      colorPickerPaletteConstants.resolveCoreValueCategoryCode(fromPicker)
    if (code) return code
  }

  const stored = String(form?.category ?? "").trim()
  if (stored && !stored.startsWith("#")) {
    return stored.toUpperCase()
  }
  if (stored.startsWith("#")) {
    return (
      colorPickerPaletteConstants.resolveCoreValueCategoryCode(stored) || stored
    )
  }

  return fromPicker
}

/**
 * @param {Record<string, unknown>} existingRow
 * @param {Record<string, unknown>} form
 * @returns {Record<string, unknown>}
 */
export function updateCoreValueRowFromForm(existingRow, form) {
  if (!existingRow?.id) {
    return buildCoreValueRowFromForm(form, getNextCoreValueId([]))
  }
  return {
    ...existingRow,
    ...buildCoreValueRowFromForm(form, existingRow.id)
  }
}

/**
 * @param {{ name?: string, description?: string, category_color?: string, status?: boolean }} form
 * @param {string[]} existingNames
 * @param {{ excludeName?: string }} [options]
 * @returns {Record<string, string>}
 */
export function validateCoreValueForm(form, existingNames = [], options = {}) {
  const excludeName = options?.excludeName
  const errors = {}
  const name = String(form?.name ?? "").trim()
  const description = String(form?.description ?? "").trim()
  const color = String(form?.category_color ?? "").trim()

  if (!name) {
    errors.name = "required"
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.name = "maxLength"
  } else {
    const excludeNormalized = String(excludeName ?? "")
      .trim()
      .toLowerCase()
    const duplicate = (existingNames || []).some((n) => {
      const normalized = String(n || "")
        .trim()
        .toLowerCase()
      if (excludeNormalized && normalized === excludeNormalized) {
        return false
      }
      return normalized === name.toLowerCase()
    })
    if (duplicate) {
      errors.name = "duplicate"
    }
  }

  if (description.length > MAX_DESCRIPTION_LENGTH) {
    errors.description = "maxLength"
  }

  const status = Number(form?.status)
  if (
    form?.status == null ||
    form?.status === "" ||
    (status !== 0 && status !== 1)
  ) {
    errors.status = "required"
  }

  if (!color || !color.startsWith("#")) {
    errors.category_color = "required"
  } else if (
    !colorPickerPaletteConstants.isHexInColorPalette(
      color,
      behaviourCoreValueConstants.CORE_VALUE_COLOR_PALETTE
    )
  ) {
    errors.category_color = "invalidColor"
  }

  return errors
}

/**
 * @param {{ name?: string, description?: string, category_color?: string, status?: number|string }} form
 * @returns {{ name: string, category: string, status: number, description: string | null }}
 */
export function buildCoreValueCreatePayload(form) {
  const description = String(form?.description ?? "").trim()
  return {
    name: String(form?.name ?? "").trim(),
    category: resolveCategoryForApi(form),
    status: Number(form?.status) === 0 ? 0 : 1,
    description: description || null
  }
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [existingRow]
 * @returns {{ name: string, category: string, status: number, description: string | null, sort_order: number | null }}
 */
export function buildCoreValueUpdatePayload(form, existingRow = {}) {
  const description = String(form?.description ?? "").trim()
  const sortRaw = form?.sort_order ?? existingRow?.sort_order
  const sortOrder =
    sortRaw != null && sortRaw !== "" && Number.isFinite(Number(sortRaw))
      ? Number(sortRaw)
      : null

  return {
    name: String(form?.name ?? "").trim(),
    category: resolveCategoryForApi(form),
    status: Number(form?.status) === 0 ? 0 : 1,
    description: description || null,
    sort_order: sortOrder
  }
}

/**
 * PUT payload for enable/disable: same fields as edit, only `status` changes (0 or 1).
 * @param {Record<string, unknown>} existingRow
 * @param {"active" | "inactive" | 0 | 1 | boolean} nextStatus
 */
export function buildCoreValueStatusUpdatePayload(existingRow, nextStatus) {
  const form = coreValueRowToForm(existingRow)
  const enabled =
    nextStatus === "active" || nextStatus === 1 || nextStatus === true
  form.status = enabled ? 1 : 0
  return buildCoreValueUpdatePayload(form, existingRow)
}

/**
 * @param {unknown} err
 * @returns {{ field: string | null, code: string, message: string }}
 */
export function parseCoreValueApiError(err) {
  const data = err?.response?.data
  const message =
    String(data?.error ?? data?.message ?? err?.message ?? "").trim() || ""
  const normalized = message.toLowerCase()

  if (
    normalized.includes("already exists") ||
    normalized.includes("duplicate")
  ) {
    return { field: "name", code: "duplicate", message }
  }

  return { field: null, code: "server", message }
}

/** @deprecated Use {@link parseCoreValueApiError} */
export const parseCreateCoreValueApiError = parseCoreValueApiError

/**
 * @param {{ name: string, description: string, category_color: string, status: string|boolean }} form
 * @param {number} nextId
 */
export function buildCoreValueRowFromForm(form, nextId) {
  const category = resolveCategoryForApi(form)
  const categoryColor = resolveCategoryColor(category) || category
  const isInactive =
    form?.status === 0 || form?.status === false || form?.status === "inactive"
  const statusValue = isInactive ? "inactive" : "active"
  return {
    id: nextId,
    name: String(form?.name ?? "").trim(),
    description: String(form?.description ?? "").trim(),
    status: statusValue,
    category,
    category_color: categoryColor,
    indicator_color: mapCategoryColorToIndicator(categoryColor)
  }
}

/**
 * @param {Array<{ id?: number }>} rows
 * @returns {number}
 */
export function getNextCoreValueId(rows) {
  const list = Array.isArray(rows) ? rows : []
  const maxId = list.reduce(
    (max, row) => Math.max(max, Number(row?.id) || 0),
    0
  )
  return maxId + 1
}
